import { LpGptHeroBlock, LpHeroBlock } from "@/puck/landingBlocks";
import type { LandingComponents } from "@/puck/blocks/blockTypes";
import { extractBlogMdxSource } from "@/utils/blogMdxSource";
import {
  ICLOSED_EVENT_TITLE,
  ICLOSED_INSURANCE_EVENT_URL,
} from "@/utils/iclosed";

export const heroConfigs: Pick<LandingComponents, "LpHero" | "LpGptHero"> = {
  LpHero: {
    label: "Marketing hero (rotating)",
    fields: {
      titleMain: { type: "text", contentEditable: true },
      titleConnector: { type: "text" },
      titleSuffix: { type: "text" },
      audiencePhrases: {
        type: "array",
        getItemSummary: (item) => extractBlogMdxSource(item.text) || "Audience",
        defaultItemProps: { text: "Audience" },
        arrayFields: { text: { type: "text", contentEditable: true } },
      },
      descLine1: {
        type: "array",
        label: "Description line 1 (segments)",
        getItemSummary: (item) => extractBlogMdxSource(item.text) || "Text",
        defaultItemProps: { text: "Text", bold: false },
        arrayFields: {
          text: { type: "text", contentEditable: true },
          bold: {
            type: "radio",
            options: [
              { label: "Normal", value: false },
              { label: "Bold", value: true },
            ],
          },
        },
      },
      descLine2: {
        type: "array",
        label: "Description line 2 (segments)",
        getItemSummary: (item) => extractBlogMdxSource(item.text) || "Text",
        defaultItemProps: { text: "Text", bold: false },
        arrayFields: {
          text: { type: "text", contentEditable: true },
          bold: {
            type: "radio",
            options: [
              { label: "Normal", value: false },
              { label: "Bold", value: true },
            ],
          },
        },
      },
      showVideo: {
        type: "radio",
        label: "Hero video",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
      videoMediaId: { type: "text", label: "Wistia media ID" },
      videoAutoplay: {
        type: "radio",
        label: "Video autoplay",
        options: [
          { label: "Off", value: false },
          { label: "On muted", value: true },
        ],
      },
      footnotes: {
        type: "array",
        getItemSummary: (item) =>
          extractBlogMdxSource(item.text) || "Proof point",
        defaultItemProps: { text: "Proof point" },
        arrayFields: { text: { type: "text", contentEditable: true } },
      },
    },
    defaultProps: {
      titleMain: "AI Debt Collector",
      titleConnector: "for",
      titleSuffix: ".",
      audiencePhrases: [
        { text: "Lenders" },
        { text: "Banks" },
        { text: "Call Centers" },
      ],
      descLine1: [
        { text: "Inbound. Outbound. Across " },
        { text: "every channel", bold: true },
        { text: "." },
      ],
      descLine2: [
        { text: "Trained on your domain. " },
        { text: "Compliant by design.", bold: true },
      ],
      showVideo: true,
      videoMediaId: "28kxqyxob7",
      videoAutoplay: true,
      footnotes: [
        { text: "Recover more without adding headcount" },
        { text: "Always-on outreach" },
      ],
    },
    render: ({ puck, ...props }) => (
      <LpHeroBlock {...props} isEditing={puck.isEditing} />
    ),
  },
  LpGptHero: {
    label: "GPT hero + offer",
    fields: {
      offerBadge: { type: "text", label: "Offer badge (empty hides badge)" },
      title: { type: "text", contentEditable: true },
      titleAccent: { type: "text", contentEditable: true },
      titleLine2: { type: "text", label: "Title line 2", contentEditable: true },
      subtitle: {
        type: "textarea",
        label: "Subtitle (plain, used when no segments)",
        contentEditable: true,
      },
      subtitleSegments: {
        type: "array",
        label: "Subtitle (segments)",
        getItemSummary: (item) => extractBlogMdxSource(item.text) || "Text",
        defaultItemProps: { text: "Text", bold: false },
        arrayFields: {
          text: { type: "text", contentEditable: true },
          bold: {
            type: "radio",
            options: [
              { label: "Normal", value: false },
              { label: "Bold", value: true },
            ],
          },
        },
      },
      ctaLabel: { type: "text", label: "CTA label (empty hides button)" },
      pillsEyebrow: { type: "text", label: "Pills eyebrow" },
      audiencePills: {
        type: "array",
        label: "Audience pills",
        getItemSummary: (item) =>
          extractBlogMdxSource(item.text) || "Audience",
        defaultItemProps: { text: "Audience" },
        arrayFields: { text: { type: "text", contentEditable: true } },
      },
      showMockup: {
        type: "radio",
        label: "Studio app mockup",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
      showVideo: {
        type: "radio",
        label: "Hero video",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
      videoMediaId: { type: "text", label: "Wistia media ID" },
      videoAutoplay: {
        type: "radio",
        options: [
          { label: "Off", value: false },
          { label: "On muted", value: true },
        ],
      },
      showOffer: {
        type: "radio",
        label: "Offer card + scheduler",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
      offerTitle: { type: "text", contentEditable: true },
      offerSubtitle: { type: "textarea", contentEditable: true },
      iclosedEventUrl: { type: "text", label: "iClosed event URL" },
      iclosedEventTitle: { type: "text", label: "iClosed event title" },
    },
    defaultProps: {
      offerBadge: "",
      title: "Meet",
      titleAccent: "Gail,",
      titleLine2: "the ChatGPT for Insurance.",
      subtitle: "",
      subtitleSegments: [
        {
          text: "Every tool you reach for to automate and run insurance, ",
          bold: false,
        },
        { text: "in one place", bold: true },
        { text: ", not ten.", bold: false },
      ],
      ctaLabel: "Book a 20-min demo",
      pillsEyebrow: "Built for the people who run the book",
      audiencePills: [
        { text: "Agencies" },
        { text: "MGAs" },
        { text: "Carriers" },
        { text: "Brokers" },
      ],
      showMockup: true,
      showVideo: false,
      videoMediaId: "cj25mopmgf",
      videoAutoplay: true,
      showOffer: false,
      offerTitle: "Start this week, get a month free.",
      offerSubtitle:
        "A $500/month value, free — book your call before Sunday to lock it in.",
      iclosedEventUrl: ICLOSED_INSURANCE_EVENT_URL,
      iclosedEventTitle: ICLOSED_EVENT_TITLE,
    },
    render: ({ puck, ...props }) => (
      <LpGptHeroBlock {...props} isEditing={puck.isEditing} />
    ),
  },
};
